import { mapGetters } from 'vuex';

import * as C from '../../styles';
import * as S from './styles';

import {
    toggleInList,
    listIncludes,
    isEllipsisActive,
    isValueExist,
} from '@util';
import { refineChannelIconName } from '@/utils/dashboard';
import { ALL_AD_ACCOUNTS_KEY } from '../constants';

import Checkbox from '@components/Common/Checkbox.js';
import CommonButton from '@design/components/button/Button';

import channelIcon from '@/mixins/channelIcons';
import SearchIcon from '@svgIcons/search.svg';

const IS_ALL = R.equals(ALL_AD_ACCOUNTS_KEY);

export default {
    name: 'AdAccountCheckbox',
    mixins: [channelIcon],
    props: {
        options: {
            type: Array,
            default() {
                return [];
            },
        },
        selectedItems: {
            type: Array,
            default() {
                return [];
            },
        },
        enableBlock: { type: Boolean, default: true },
        normalizeText: {
            type: Function,
        },
        allowEmptyValue: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            selectedAdAccountIdList: this.selectedItems,
            keyword: '',
        };
    },
    computed: {
        ...mapGetters('newDashboard', ['adAccounts']),
        filteredAdAccountList() {
            const { keyword, options, normalizeText, adAccountFormatter } =
                this;
            const filterText = normalizeText(keyword);

            return keyword
                ? options.filter(({ name, channelId, channel, title }) => {
                      const { accountName, accountId } = adAccountFormatter({
                          name,
                          channel,
                          channelId,
                          title,
                      });

                      return [accountName, accountId, channel].some(
                          (v) => normalizeText(v).indexOf(filterText) > -1
                      );
                  })
                : options;
        },
        allAdAccounts: R.compose(
            R.pluck('channelId'),
            R.propOr([], 'adAccounts')
        ),
    },
    methods: {
        adAccountFormatter({ name, channel, channelId, title }) {
            const { adAccounts, allAdAccounts, selectedAdAccountIdList } = this;

            const allAdAccountsSelected = R.includes(
                ALL_AD_ACCOUNTS_KEY,
                selectedAdAccountIdList
            );
            const allAdAccountsText = this.$t(
                `FILTERS.adAccountIds_option_${ALL_AD_ACCOUNTS_KEY}`
            );
            const adAccountsChannelIds = R.pluck('channelId')(adAccounts);

            const isAllAdAccount = R.equals(ALL_AD_ACCOUNTS_KEY, name);

            const accountName = isAllAdAccount
                ? `${allAdAccountsText} (${adAccountsChannelIds.length})`
                : channel === 'googleSpreadSheet' && title
                ? `${title} | ${name}`
                : name;

            const accountId = isAllAdAccount
                ? allAdAccountsSelected
                    ? allAdAccounts.join(', ')
                    : ''
                : channelId;

            return { accountName, accountId };
        },
        updateSelectedItem(id) {
            const {
                selectedAdAccountIdList: list,
                selectedItems,
                enableBlock,
            } = this;

            const shouldUnselectAll = IS_ALL(id) && R.none(IS_ALL, list);

            this.selectedAdAccountIdList = toggleInList({
                pred: R.equals(id),
                list: shouldUnselectAll
                    ? []
                    : enableBlock
                    ? [...selectedItems, ...list]
                    : list,
                value: id,
            });
        },
        shouldDisplayTooltip(channelId) {
            const $el = document.getElementById(`${channelId}-name`);

            return $el ? isEllipsisActive($el) : false;
        },
        getDataList() {
            const {
                selectedAdAccountIdList,
                updateSelectedItem,
                filteredAdAccountList,
                adAccounts,
                shouldDisplayTooltip,
                selectedItems,
                enableBlock,
            } = this;

            const hasData = filteredAdAccountList.length > 0;
            const allAdAccountsSelected = R.includes(
                ALL_AD_ACCOUNTS_KEY,
                selectedAdAccountIdList
            );

            const adAccountsChannelIds = R.pluck('channelId')(adAccounts);
            const withoutEqualToAdAccounts =
                R.complement(listIncludes)(adAccountsChannelIds);

            const blockedItemTooltip = this.$t(
                'FILTERS.blocked_item_tooltip_msg'
            );

            return hasData ? (
                <C.ListWrapper>
                    {filteredAdAccountList
                        .filter(({ channelId }) =>
                            allAdAccountsSelected
                                ? withoutEqualToAdAccounts(channelId)
                                : R.always(true)
                        )
                        .map(
                            ({
                                name,
                                channelId,
                                channel,
                                title,
                                logo,
                                status,
                            }) => {
                                const foundAdAccount =
                                    selectedAdAccountIdList.find(
                                        R.equals(channelId)
                                    );

                                const blockedItem = selectedItems.find(
                                    R.equals(channelId)
                                );

                                const isBlocked =
                                    enableBlock && isValueExist(blockedItem);

                                const { accountName, accountId } =
                                    this.adAccountFormatter({
                                        name,
                                        channel,
                                        channelId,
                                        title,
                                    });

                                const applyQMark =
                                    ['adverity', 'airbyte'].indexOf(channel) >
                                        -1 &&
                                    status &&
                                    status !== 'ACTIVE';

                                const tooltip = (() => {
                                    if (applyQMark) {
                                        return this.$t(
                                            `CONNECTION.dashboard_status_${status}`
                                        );
                                    }
                                })();

                                return (
                                    <C.ItemWrapper
                                        onClick={() => {
                                            if (tooltip || isBlocked) return;
                                            updateSelectedItem(channelId);
                                        }}
                                        onMouseenter={() => this.$forceUpdate()}
                                    >
                                        <Checkbox
                                            value={
                                                Boolean(foundAdAccount) ||
                                                isBlocked
                                            }
                                            tooltip={
                                                isBlocked
                                                    ? blockedItemTooltip
                                                    : tooltip
                                            }
                                            isBlock={
                                                Boolean(tooltip) || isBlocked
                                            }
                                            forceQmark={applyQMark}
                                        />

                                        <C.Logo>
                                            {logo ? (
                                                <S.InternalLogo src={logo} />
                                            ) : (
                                                this.channelIconWithAdverity(
                                                    refineChannelIconName(
                                                        channel
                                                    )
                                                )
                                            )}
                                        </C.Logo>

                                        {this.wrapTooltip({
                                            visible:
                                                shouldDisplayTooltip(channelId),
                                            title: accountName,
                                            content: (
                                                <S.Title
                                                    id={`${channelId}-name`}
                                                >
                                                    {accountName}
                                                </S.Title>
                                            ),
                                            className: 'break-all',
                                        })}
                                        {this.wrapTooltip({
                                            visible:
                                                allAdAccountsSelected ||
                                                shouldDisplayTooltip(channelId),
                                            title: accountId,
                                            content: (
                                                <S.Subtitle
                                                    id={`${channelId}-id`}
                                                >
                                                    {accountId}
                                                </S.Subtitle>
                                            ),
                                            className: 'break-all',
                                        })}
                                    </C.ItemWrapper>
                                );
                            }
                        )}
                </C.ListWrapper>
            ) : (
                <div
                    class="no-data"
                    domPropsInnerHTML={this.$t(
                        'NEW_DASHBOARD.filter_search_no_data'
                    )}
                ></div>
            );
        },
    },
    mounted() {
        const inputBox = this.$refs.keywordInputbox;
        inputBox && inputBox.focus();
    },
    render() {
        const { selectedAdAccountIdList = [], filteredAdAccountList } = this;

        const hasData = filteredAdAccountList.length > 0;
        const hasSelectedAdAccountIdList = selectedAdAccountIdList.length > 0;

        return (
            <C.Wrapper>
                <C.SearchHeader>
                    <input
                        onInput={(e) => {
                            this.keyword = e.target.value;
                        }}
                        placeholder={this.$t('NEW_DASHBOARD.filter_search')}
                        class={'search-bar'}
                        ref={'keywordInputbox'}
                    ></input>
                    <div class="icon-wrapper">
                        <SearchIcon v-view-box width="16px" height="16px" />
                    </div>
                </C.SearchHeader>
                <C.ResultWrapper hasData={hasData}>
                    {this.getDataList()}
                </C.ResultWrapper>
                <C.ButtonWrapper>
                    {hasSelectedAdAccountIdList && (
                        <CommonButton
                            class="unselect-btn"
                            type="secondary"
                            onClick={() => {
                                this.selectedAdAccountIdList = [];
                            }}
                        >
                            {this.$t('COMMON.unselect_all')}
                        </CommonButton>
                    )}

                    <CommonButton
                        disabled={
                            !hasSelectedAdAccountIdList && !this.allowEmptyValue
                        }
                        onClick={() => {
                            if (
                                hasSelectedAdAccountIdList ||
                                this.allowEmptyValue
                            ) {
                                const adAccountIdList = this.enableBlock
                                    ? selectedAdAccountIdList.filter(
                                          R.compose(
                                              R.complement,
                                              R.includes(
                                                  R.__,
                                                  this.selectedItems
                                              )
                                          )
                                      )
                                    : selectedAdAccountIdList;

                                this.$emit('submit', adAccountIdList);
                            }
                        }}
                    >
                        {this.$t('COMMON.apply')}
                    </CommonButton>
                </C.ButtonWrapper>
            </C.Wrapper>
        );
    },
};
